import { NavLink, useLocation } from "react-router-dom";
import { isMobile } from 'react-device-detect';
import React, { Fragment, useState } from 'react';
import { motion } from 'framer-motion';
import logo from '../assets/RGB_LilyLaiLogo_White.png';
import logoBlack from '../assets/RGB_LilyLaiLogo_Black.png';
import '../styles/Navbar.scss';
import hamburger from '../assets/hamburger.svg';
import close from '../assets/close.svg';
import { Icon } from "semantic-ui-react";
import { useLockBodyScroll, useToggle } from 'react-use';

const heroContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const heroWord = {
  hidden: { opacity: 0, y: 52 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const heroFade = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

// Pages with a dark hero/background use the light (white) logo. Everything else —
// including unknown/404 paths — uses the dark logo on a light background.
const LIGHT_LOGO_PAGES = ['', 'contact', 'testimonials'];
const isDark = (loc) => !LIGHT_LOGO_PAGES.includes(loc);

const Navbar = () => {
  const [showNavbar, setShowNavbar] = useState(false);
  const [locked, setLocked] = useToggle(false);
  useLockBodyScroll(locked);
  const path = useLocation().pathname;
  const location = path.split("/")[1].toLowerCase();
  const handleShowNavbar = () => {
    setShowNavbar(!showNavbar);
    isMobile ? setLocked(!locked) : setLocked(false);
  };

  const logoSrc = isDark(location) ? logoBlack : logo;

  return (
    <nav className={`navbar header-${location}`}>
      {location === '' && !isMobile && (
        <video
          src='https://res.cloudinary.com/dlaituqwq/video/upload/f_auto:video,q_70,w_1280/nyqditwg1sqyqollk3if'
          poster='https://res.cloudinary.com/dlaituqwq/video/upload/so_0,f_jpg,q_auto,w_1280/nyqditwg1sqyqollk3if.jpg'
          autoPlay
          muted
          loop
          playsInline
          preload='auto'
        />
      )}

      {/* ── Desktop split nav (≥768px) ── */}
      <div className="nav-split">
        <div className="nav-split-left">
          <NavLink to="/neighborhoods" className='link-underline'>Neighborhoods</NavLink>
          <NavLink to="/home-valuation" className='link-underline'>Home Valuation</NavLink>
        </div>

        <div className="nav-split-logo">
          <NavLink to='/'>
            <img loading='eager' src={logoSrc} title='logo' className='logo-image' alt="Lily Lai Real Estate" />
          </NavLink>
        </div>

        <div className="nav-split-right">
          <NavLink to="/insights" className='link-underline'>Insights</NavLink>
          <NavLink to="/testimonials" className='link-underline'>Testimonials</NavLink>
          <NavLink to="/about" className='link-underline'>About</NavLink>
          <NavLink to="/contact" className='link-underline nav-contact-btn'>Contact</NavLink>
        </div>
      </div>

      {/* ── Mobile nav (<768px) ── */}
      <div className="logo logo-mobile">
        <NavLink to='/'>
          <img loading='eager' src={logoSrc} title='logo' className='logo-image' alt="Lily Lai Real Estate" />
        </NavLink>
      </div>
      <div className="container">
        <button
          className="menu-icon"
          onClick={handleShowNavbar}
          aria-label={showNavbar ? 'Close menu' : 'Open menu'}
          aria-expanded={showNavbar}
          aria-controls="mobile-nav"
        >
          <img loading='eager' title='hamburger-menu' alt='' src={!showNavbar ? hamburger : close} className='hamburger' aria-hidden="true" />
        </button>
        <div className='contact-info'>
          <a
            href='tel:4157282623'
            onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'nav_phone_click'); }}
          ><Icon name='phone' />415.728.2623</a>
          <a
            href='mailto:lily@lilylaisf.com'
            onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'nav_email_click'); }}
          ><Icon name='mail' />lily@lilylaisf.com</a>
        </div>
        <div id="mobile-nav" className={`nav-elements ${showNavbar && "active"}`}>
          <ul>
            {!isMobile && <Fragment>
              <li>{/* <NavLink to="/buyers">Buyers</NavLink> */}</li>
              <li>{/* <NavLink to="/sellers">Sellers</NavLink> */}</li>
              <li>{/* <NavLink to="/market-updates">Market Updates</NavLink> */}</li>
            </Fragment>}
            <li><NavLink to="/neighborhoods" onClick={handleShowNavbar} className='link-underline'>Neighborhoods</NavLink></li>
            <li><NavLink to="/home-valuation" onClick={handleShowNavbar} className='link-underline'>Home Valuation</NavLink></li>
            <li><NavLink to="/insights" onClick={handleShowNavbar} className='link-underline'>Insights</NavLink></li>
            <li><NavLink to="/testimonials" onClick={handleShowNavbar} className='link-underline'>Testimonials</NavLink></li>
            <li><NavLink to="/about" onClick={handleShowNavbar} className='link-underline'>About</NavLink></li>
            <li><NavLink to="/contact" onClick={handleShowNavbar} className='link-underline'>Contact</NavLink></li>
          </ul>
        </div>
      </div>

      {location === '' && (
        <motion.div
          className='tagline'
          variants={heroContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div className='tagline-heading' variants={heroContainer}>
            {'MAKERS OF MEANINGFUL CHANGE'.split(' ').map((word, i) => (
              <motion.span
                key={i}
                variants={heroWord}
                style={{ display: 'inline-block', marginRight: '0.25em' }}
              >
                {word}
              </motion.span>
            ))}
          </motion.div>
          <motion.p className='tagline-sub' variants={heroFade}>
            <span className='tagline-sub-line1'>A thoughtful, modern approach to buying and selling real estate in the Bay Area.</span>
            <br />
            Guided with care, clarity, and local expertise.
          </motion.p>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
